import React from 'react';
import useDialog from '../hooks/useDialog';
import { useMonetization } from '../contexts/MonetizationContext';
import RewardedAdButton from './ads/RewardedAdButton';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { useTranslation } from '../contexts/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faLightbulb, faShoppingCart, faCoins } from '@fortawesome/free-solid-svg-icons';
import './HintUnlockModal.css';

/**
 * HintUnlockModal - Shown when user needs hints but has used their 2 free hints.
 *
 * Options:
 * 1. Watch Ad (if ads not removed) - grants 1 hint
 * 2. Buy Hints - opens purchase modal
 * 3. Cancel
 */
function HintUnlockModal({ onHintGranted }) {
    const {
        showHintUnlockModal,
        closeHintUnlockModal,
        openPurchaseModal,
        purchasesEnabled,
        hintBalance,
        adsRemoved,
        freeHintsRemaining,
        isNativeMobile,
    } = useMonetization();
    const { t, locale } = useTranslation();

    useBodyScrollLock(showHintUnlockModal);
    const dialogRef = useDialog(!!showHintUnlockModal, closeHintUnlockModal);

    if (!showHintUnlockModal) {
        return null;
    }

    const handleRewardGranted = () => {
        closeHintUnlockModal();
        onHintGranted?.();
    };

    const handleBuyHints = () => {
        closeHintUnlockModal();
        openPurchaseModal();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            closeHintUnlockModal();
        }
    };

    return (
        <div className="modal-overlay-base hint-unlock-overlay" onClick={handleOverlayClick}>
            <div className="modal-content-base hint-unlock-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true">
                <button
                    className="hint-unlock-close"
                    onClick={closeHintUnlockModal}
                    aria-label={t('common.close')}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                <div className="hint-unlock-header">
                    <div className="hint-unlock-icon">
                        <FontAwesomeIcon icon={faLightbulb} />
                    </div>
                    <h2>{t('game.hintUnlock.title')}</h2>
                    <p className="hint-unlock-subtitle">
                        {t('game.hintUnlock.subtitle')}
                    </p>
                </div>

                <div className="hint-unlock-balance">
                    <FontAwesomeIcon icon={faCoins} />
                    <span>{t.rich('game.hintUnlock.balance', { b: <strong /> }, { count: hintBalance })}</span>
                </div>

                <div className="hint-unlock-options">
                    {/* Watch Ad option (all platforms, if ads not removed) */}
                    {!adsRemoved && (
                        <div className="hint-unlock-option">
                            <RewardedAdButton
                                onRewardGranted={handleRewardGranted}
                                onError={(error) => console.error('Ad error:', error)}
                            />
                            <p className="option-description">
                                {t('game.hintUnlock.watchAd')}
                            </p>
                        </div>
                    )}

                    {/* Buy Hints option (hidden when purchases disabled) */}
                    {purchasesEnabled && (
                        <div className="hint-unlock-option">
                            <button
                                className="buy-hints-button"
                                onClick={handleBuyHints}
                            >
                                <FontAwesomeIcon icon={faShoppingCart} />
                                <span>{t('game.hintUnlock.buy')}</span>
                            </button>
                            <p className="option-description">
                                {t('game.hintUnlock.startingAt', { count: 10, price: (0.99).toLocaleString(locale, { style: 'currency', currency: 'EUR' }) })}
                            </p>
                        </div>
                    )}
                </div>

                {/* Cancel button */}
                <button
                    className="hint-unlock-cancel"
                    onClick={closeHintUnlockModal}
                >
                    {t('game.hintUnlock.maybeLater')}
                </button>
            </div>
        </div>
    );
}

export default HintUnlockModal;
